// src/pages/purchases/PurchaseDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../utils/config";

// --- helpers ---
const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
    Number(n || 0)
  );

const ddmmyyyy = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}-${mm}-${yy}`;
};

const isExpiringOrPast = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d)) return false;
  const now = new Date();
  // mark red if expiry <= end of current month
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return d.getTime() <= monthEnd.getTime();
};

export default function PurchaseDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/api/purchases/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        console.error(e);
        setErr("Failed to load purchase.");
      }
    })();
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("Delete this purchase?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/api/purchases/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate("/purchases");
    } catch (e) {
      console.error("Delete error:", e);
      alert("Failed to delete");
    }
  };

  const totals = useMemo(() => {
    if (!data?.items) return { qty: 0, gst: 0, value: 0, subtotal: 0 };
    let qty = 0,
      gst = 0,
      value = 0;
    for (const it of data.items) {
      const lineVal = Number(it.value || 0);
      const lineGst = (lineVal * Number(it.tax_percent || 0)) / 100;
      qty += Number(it.quantity || 0);
      gst += lineGst;
      value += lineVal;
    }
    const subtotal = value - gst;
    return { qty, gst, value, subtotal };
  }, [data]);

  if (!data) {
    return (
      <div className="p-6">
        {err ? (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-700">
            {err}
          </div>
        ) : (
          <div className="animate-pulse h-8 w-48 bg-gray-200 rounded mb-4" />
        )}
        <div className="animate-pulse h-72 w-full bg-gray-100 rounded" />
      </div>
    );
  }

  const order = data.order || {};
  const headerSubtotal =
    order.total_amount != null && order.total_gst != null
      ? Number(order.total_amount) - Number(order.total_gst)
      : totals.subtotal;

  return (
    <div className="p-4 md:p-6 w-full">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl font-semibold tracking-tight">Purchase Details</h2>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            className="px-3 py-2 rounded-xl border border-gray-300 hover:bg-gray-50 text-sm"
          >
            Print
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-2 rounded-xl border border-gray-300 hover:bg-gray-50 text-sm"
          >
            Back
          </button>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 md:p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="col-span-2">
            <div className="text-xs text-gray-500">Supplier</div>
            <div className="text-sm md:text-base font-medium text-gray-900">
              {order.supplier_name || "-"}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Invoice #</div>
            <div className="text-sm font-medium">{order.invoice_number || "-"}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Date</div>
            <div className="text-sm font-medium">{ddmmyyyy(order.invoice_date)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Subtotal</div>
            <div className="text-sm font-semibold">{inr(headerSubtotal)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">GST</div>
            <div className="text-sm font-semibold">
              {inr(order.total_gst != null ? order.total_gst : totals.gst)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Total</div>
            <div className="text-base font-bold text-gray-900">
              {inr(order.total_amount != null ? order.total_amount : totals.value)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Created By</div>
            <div className="text-sm font-medium">{order.created_by || "-"}</div>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-lg font-semibold">Items</h3>
        <div className="text-sm text-gray-600">
          Rows: <span className="font-medium">{data.items?.length || 0}</span>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-xs uppercase tracking-wide text-gray-600">
                <th className="px-3 py-3 text-left">Sr. No.</th>
                <th className="px-3 py-3 text-left">Product</th>
                <th className="px-3 py-3 text-left">Category</th>
                <th className="px-3 py-3 text-left">Batch</th>
                <th className="px-3 py-3 text-left">Expiry</th>
                <th className="px-3 py-3 text-right">Qty</th>
                <th className="px-3 py-3 text-right">MRP</th>
                <th className="px-3 py-3 text-right">Rate</th>
                <th className="px-3 py-3 text-right">GST value</th>
                <th className="px-4 py-3 text-right">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.items.map((it, i) => {
                const gstVal =
                  (Number(it.value || 0) * Number(it.tax_percent || 0)) / 100;
                const expBad = isExpiringOrPast(it.expiry_date);
                return (
                  <tr key={i} className="hover:bg-gray-50/70">
                    <td className="px-3 py-2.5 text-sm text-gray-600">{i + 1}</td>
                    <td className="px-3 py-2.5">
                      <div className="text-sm font-medium text-gray-900">
                        {it.product_name}
                      </div>
                      {it.hsn_code && (
                        <div className="text-[11px] text-gray-500">HSN: {it.hsn_code}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-700">
                      {it.category_name || "-"}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="inline-block rounded-md bg-gray-100 border border-gray-200 px-2 py-1 text-xs font-mono">
                        {it.batch_no || it.batch_number || "-"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`inline-flex items-center rounded-md px-2 py-1 text-xs ${
                          expBad
                            ? "bg-red-50 text-red-700 border border-red-200"
                            : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        }`}
                      >
                        {ddmmyyyy(it.expiry_date)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {Number(it.quantity || 0)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {inr(it.mrp || 0)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {inr(it.rate || 0)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {inr(gstVal)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums">
                      {inr(it.value || 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td className="px-3 py-3 text-sm text-gray-600" colSpan={5}>
                  Totals
                </td>
                <td className="px-3 py-3 text-right font-semibold tabular-nums">
                  {totals.qty}
                </td>
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />
                <td className="px-3 py-3 text-right font-semibold tabular-nums">
                  {inr(totals.gst)}
                </td>
                <td className="px-4 py-3 text-right font-bold tabular-nums">
                  {inr(totals.value)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 flex flex-wrap gap-3">
        <Link
          to={`/purchases/edit/${id}`}
          className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl"
        >
          Edit
        </Link>
        <button
          onClick={handleDelete}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl"
        >
          Delete
        </button>
      </div>
    </div>
  );
}
